import { Fragment, useState, type ReactNode } from 'react';
import { cn } from '@playerbase/ui';

// ---------------------------------------------------------------------------
// Discord-flavoured inline markdown for chat content in the feed.
//
// Supports the inline subset that actually shows up in player feedback:
// **bold**, *italic* / _italic_, ***bold-italic***, __underline__,
// ~~strike~~, `inline code`, ||spoiler||, [label](url), and bare
// http(s) URLs. Block-level markdown (code fences, blockquotes,
// headers, lists) is intentionally not rendered - those are rare in
// chat and the parser stays simple.
//
// Parser is a recursive descent over an ordered pattern table. At each
// position we try patterns in precedence order; the first match wins
// and its inner content is recursively tokenised (so **bold _and_
// italic** works). Code is opaque - its contents are rendered verbatim
// without further tokenisation, matching Discord's rules. Plain text
// between matches accumulates into a text token.
//
// _italic_ is gated on a non-word boundary on each side so snake_case
// in feedback ("the my_var_name issue...") doesn't accidentally
// italicise. *italic* has no such constraint, again matching Discord.
// ---------------------------------------------------------------------------

interface DiscordMarkdownProps {
  text: string;
}

interface Token {
  kind: 'text' | 'bold' | 'italic' | 'underline' | 'strike' | 'code' | 'spoiler' | 'link';
  text?: string;
  children?: Token[];
  href?: string;
}

interface Pattern {
  regex: RegExp;
  // Word-boundary check for `_italic_` style. When true we only match
  // if the char immediately before the position is not a word char.
  requireLeftWordBoundary?: boolean;
  build: (m: RegExpExecArray) => Token;
}

const PATTERNS: Pattern[] = [
  {
    regex: /^`([^`\n]+)`/,
    build: (m) => ({ kind: 'code', text: m[1]! }),
  },
  {
    regex: /^\|\|([\s\S]+?)\|\|/,
    build: (m) => ({ kind: 'spoiler', children: tokenize(m[1]!) }),
  },
  {
    regex: /^\*\*\*([\s\S]+?)\*\*\*/,
    build: (m) => ({
      kind: 'bold',
      children: [{ kind: 'italic', children: tokenize(m[1]!) }],
    }),
  },
  {
    regex: /^\*\*([\s\S]+?)\*\*/,
    build: (m) => ({ kind: 'bold', children: tokenize(m[1]!) }),
  },
  {
    regex: /^__([\s\S]+?)__/,
    build: (m) => ({ kind: 'underline', children: tokenize(m[1]!) }),
  },
  {
    regex: /^~~([\s\S]+?)~~/,
    build: (m) => ({ kind: 'strike', children: tokenize(m[1]!) }),
  },
  {
    regex: /^\*([^*\n]+?)\*/,
    build: (m) => ({ kind: 'italic', children: tokenize(m[1]!) }),
  },
  {
    regex: /^_([^_\n]+?)_(?!\w)/,
    requireLeftWordBoundary: true,
    build: (m) => ({ kind: 'italic', children: tokenize(m[1]!) }),
  },
  {
    regex: /^\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/,
    build: (m) => ({ kind: 'link', text: m[1]!, href: m[2]! }),
  },
  // Bare URL. Trailing punctuation is excluded so a URL at the end of
  // a sentence ("see https://example.com.") doesn't pull the period
  // into the link.
  {
    regex: /^(https?:\/\/[^\s<>"`]+[^\s<>"`.,;:!?])/,
    build: (m) => ({ kind: 'link', text: m[1]!, href: m[1]! }),
  },
];

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  let buffer = '';
  let i = 0;

  const flush = (): void => {
    if (buffer.length > 0) {
      tokens.push({ kind: 'text', text: buffer });
      buffer = '';
    }
  };

  while (i < text.length) {
    const slice = text.slice(i);
    const prevChar = i === 0 ? '' : text[i - 1] ?? '';
    let matched = false;

    for (const pat of PATTERNS) {
      if (pat.requireLeftWordBoundary === true && /\w/.test(prevChar)) continue;
      const m = pat.regex.exec(slice);
      if (m === null) continue;
      flush();
      tokens.push(pat.build(m));
      i += m[0].length;
      matched = true;
      break;
    }

    if (!matched) {
      buffer += text[i];
      i += 1;
    }
  }
  flush();
  return tokens;
}

function renderChildren(t: Token): ReactNode {
  if (t.children !== undefined) return renderTokens(t.children);
  return t.text;
}

function renderTokens(tokens: Token[]): ReactNode[] {
  return tokens.map((t, idx) => {
    const key = idx;
    switch (t.kind) {
      case 'text':
        return <Fragment key={key}>{t.text}</Fragment>;
      case 'bold':
        return (
          <strong key={key} className="font-semibold">
            {renderChildren(t)}
          </strong>
        );
      case 'italic':
        return <em key={key}>{renderChildren(t)}</em>;
      case 'underline':
        return (
          <span key={key} className="underline underline-offset-2">
            {renderChildren(t)}
          </span>
        );
      case 'strike':
        return (
          <span key={key} className="line-through">
            {renderChildren(t)}
          </span>
        );
      case 'code':
        return (
          <code
            key={key}
            className="rounded bg-surface-3 px-1 py-0.5 font-mono text-[0.85em]"
          >
            {t.text}
          </code>
        );
      case 'spoiler':
        return <Spoiler key={key}>{renderChildren(t)}</Spoiler>;
      case 'link':
        return (
          <a
            key={key}
            href={t.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-info-text underline underline-offset-2 hover:text-info"
          >
            {t.text}
          </a>
        );
    }
  });
}

// Click/keyboard-revealable spoiler. Hidden state preserves the
// inline width of the content so the surrounding text doesn't reflow
// when revealed.
function Spoiler({ children }: { children: ReactNode }): JSX.Element {
  const [revealed, setRevealed] = useState<boolean>(false);
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => setRevealed(true)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setRevealed(true);
        }
      }}
      title={revealed ? undefined : 'Click to reveal spoiler'}
      className={cn(
        'rounded px-1 transition-colors',
        revealed
          ? 'bg-surface-3'
          : 'cursor-pointer bg-text/85 text-transparent hover:bg-text/70',
      )}
    >
      {children}
    </span>
  );
}

export function DiscordMarkdown({ text }: DiscordMarkdownProps): JSX.Element {
  return <>{renderTokens(tokenize(text))}</>;
}
